import React, { useState, lazy } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { MessageCircle, Calendar, ArrowRight, MapPin, Microscope, Scan, MonitorPlay, ShieldCheck, Languages, CreditCard, Phone, Star, Plus, Minus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../i18n';
import { CallbackForm } from '../components/CallbackForm';
import { CallbackModal } from '../components/CallbackModal';
import { GoogleReviews } from '../components/GoogleReviews';
const BOOKING_URL = 'https://denticoclinic.patientportal.onl/bookings';
const WHATSAPP_URL = 'https://wa.me/447345197820';
const MAPS_URL = 'https://maps.app.goo.gl/Fhof2j1jnLA3ffqT6';
const MAPS_EMBED_URL = 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1241.0181846982155!2d-0.104187760804084!3d51.53089276153758!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x48760f7c6534e077%3A0xa54a590bd3b2a149!2sDentico%20Clinic%20-%20Private%20Dentist%20in%20Angel%2C%20Islington!5e0!3m2!1suk!2suk!4v1769722166469!5m2!1suk!2suk';
function FaqAccordionItem({
  question,
  answer,
  isOpen,
  onToggle
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return <div className="border-b border-gray-100 last:border-b-0" data-id="element-1506">
      <button onClick={onToggle} className="w-full flex items-center justify-between py-4 text-left focus:outline-none group" data-id="element-1507">
        <span className="text-lg font-medium text-gray-900 group-hover:text-teal-600 transition-colors" data-id="element-1508">
          {question}
        </span>
        <span className={`flex-shrink-0 ml-4 w-6 h-6 rounded-full flex items-center justify-center transition-colors ${isOpen ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-400'}`} data-id="element-1509">
          {isOpen ? <Minus className="w-4 h-4" data-id="element-1510" /> : <Plus className="w-4 h-4" data-id="element-1511" />}
        </span>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 opacity-100 pb-4' : 'max-h-0 opacity-0'}`} data-id="element-1512">
        <p className="text-gray-600 leading-relaxed" data-id="element-1513">{answer}</p>
      </div>
    </div>;
}
export function HomePage() {
  const {
    t,
    lang
  } = useTranslation();
  const [isCallbackOpen, setIsCallbackOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };
  const faqs = [{
    q: 'Чи є український стоматолог у Лондоні?',
    a: 'Так, Dentico Clinic — приватна стоматологічна клініка в Angel, Central London (EC1V 2PY), де лікарі консультують українською мовою.'
  }, {
    q: 'Де знайти українську стоматологію в центрі Лондона?',
    a: 'Dentico Clinic розташована за адресою 360-364 City Road, London EC1V 2PY — 5 хвилин від станції Angel. Ми пропонуємо повний спектр стоматологічних послуг українською мовою.'
  }, {
    q: 'Чи можна записатися онлайн?',
    a: 'Так, ви можете записатися онлайн через наш сайт, WhatsApp або Telegram. Запис доступний 24/7.'
  }, {
    q: 'Чи приймає клініка біля Angel Station?',
    a: 'Так, Dentico Clinic знаходиться в 5 хвилинах ходьби від Angel Station (Northern Line). Адреса: 360-364 City Road, EC1V 2PY.'
  }];
  const services = [{
    id: 'fillings',
    title: t('services.fillings.title'),
    desc: t('services.fillings.desc'),
    image: 'https://images.unsplash.com/photo-1606811841689-23dfddce3e95?auto=format&fit=crop&w=800&q=80'
  }, {
    id: 'root-canal',
    title: t('services.rootCanal.title'),
    desc: t('services.rootCanal.desc'),
    image: 'https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?auto=format&fit=crop&w=800&q=80'
  }, {
    id: 'crowns',
    title: t('services.crowns.title'),
    desc: t('services.crowns.desc'),
    image: 'https://images.unsplash.com/photo-1609840114035-3c981b782dfe?auto=format&fit=crop&w=800&q=80'
  }, {
    id: 'hygiene',
    title: t('services.hygiene.title'),
    desc: t('services.hygiene.desc'),
    image: 'https://images.unsplash.com/photo-1598256989800-fe5f95da9787?auto=format&fit=crop&w=800&q=80'
  }, {
    id: 'whitening',
    title: t('services.whitening.title'),
    desc: t('services.whitening.desc'),
    image: 'https://images.unsplash.com/photo-1606265752439-1f18756aa5fc?auto=format&fit=crop&w=800&q=80'
  }, {
    id: 'smile-makeover',
    title: t('services.smileMakeover.title'),
    desc: t('services.smileMakeover.desc'),
    image: 'https://images.unsplash.com/photo-1579684385127-1ef15d508118?auto=format&fit=crop&w=800&q=80'
  }];
  const advantages = [{
    icon: Microscope,
    title: lang === 'en' ? 'Dental Microscope' : lang === 'ua' ? 'Стоматологічний мікроскоп' : 'Стоматологический микроскоп',
    desc: lang === 'en' ? 'Higher precision for root canals' : lang === 'ua' ? 'Вища точність лікування каналів' : 'Высокая точность лечения каналов'
  }, {
    icon: Scan,
    title: lang === 'en' ? '3D Digital Scanner' : lang === 'ua' ? 'Цифровий 3D сканер' : 'Цифровой 3D сканер',
    desc: lang === 'en' ? 'No messy impressions needed' : lang === 'ua' ? 'Без відбитків — миттєвий результат' : 'Без слепков — мгновенный результат'
  }, {
    icon: MonitorPlay,
    title: lang === 'en' ? 'Digital Smile Design' : lang === 'ua' ? 'Цифровий дизайн посмішки' : 'Цифровой дизайн улыбки',
    desc: lang === 'en' ? 'See your result before treatment' : lang === 'ua' ? 'Побачте результат до лікування' : 'Увидьте результат до лечения'
  }, {
    icon: ShieldCheck,
    title: lang === 'en' ? 'UK Sterilisation Standards' : lang === 'ua' ? 'Стандарти стерилізації UK' : 'Стандарты стерилизации UK',
    desc: lang === 'en' ? 'HTM 01-05 — safety you can trust' : lang === 'ua' ? 'HTM 01-05 — безпека, якій довіряють' : 'HTM 01-05 — безопасность, которой доверяют'
  }, {
    icon: Languages,
    title: lang === 'en' ? 'Multilingual Team' : lang === 'ua' ? 'Багатомовна команда' : 'Многоязычная команда',
    desc: lang === 'en' ? 'English · Ukrainian · Russian' : lang === 'ua' ? 'Українська · English · Російська' : 'Русский · English · Українська'
  }, {
    icon: CreditCard,
    title: lang === 'en' ? 'Transparent Pricing' : lang === 'ua' ? 'Прозорі ціни' : 'Прозрачные цены',
    desc: lang === 'en' ? 'No hidden fees — ever' : lang === 'ua' ? 'Жодних прихованих платежів' : 'Никаких скрытых платежей'
  }];
  const badgeColors = {
    en: {
      bg: 'bg-teal-100',
      text: 'text-teal-800',
      dot: 'bg-teal-500'
    },
    ua: {
      bg: 'bg-blue-50',
      text: 'text-blue-700',
      border: 'border border-blue-100',
      dot: 'bg-blue-500'
    },
    ru: {
      bg: 'bg-indigo-50',
      text: 'text-indigo-700',
      border: 'border border-indigo-100',
      dot: 'bg-indigo-500'
    }
  };
  const colors = badgeColors[lang];
  return <Layout data-id="element-1514">
      {/* Callback Modal */}
      <CallbackModal isOpen={isCallbackOpen} onClose={() => setIsCallbackOpen(false)} data-id="element-1515" />

      {/* Hero Section */}
      <section className="relative bg-white overflow-hidden" data-id="element-1516">
        <div className="absolute inset-0 bg-teal-50/50 -skew-y-3 origin-top-left transform translate-y-20" data-id="element-1517" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-12 lg:pt-16 lg:pb-20 relative" data-id="element-1518">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center" data-id="element-1519">
            <div className="space-y-5 lg:space-y-6 order-2 lg:order-1" data-id="element-1520">
              <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${colors.bg} ${colors.text} ${colors.border || ''} text-sm font-medium`} data-id="element-1521">
                <span className="relative flex h-2 w-2" data-id="element-1522">
                  {lang === 'en' && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75" data-id="element-1523"></span>}
                  <span className={`relative inline-flex rounded-full h-2 w-2 ${colors.dot}`} data-id="element-1524"></span>
                </span>
                {t('hero.badge')}
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight tracking-tight" data-id="element-1525">
                {t('hero.title')}{' '}
                <span className="text-teal-600" data-id="element-1526">
                  {t('hero.titleHighlight')}
                </span>
              </h1>

              <p className="text-lg text-gray-600 max-w-md" data-id="element-1527">
                {lang === 'en' ? 'Modern private dentistry with advanced technology and transparent fees.' : lang === 'ua' ? 'Сучасна приватна стоматологія з передовими технологіями та чесними цінами.' : 'Современная частная стоматология с передовыми технологиями и честными ценами.'}
              </p>

              <div className="flex flex-col sm:flex-row gap-3" data-id="element-1528">
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:flex-1" data-id="element-1529">
                  <Button size="lg" fullWidth className="gap-2 shadow-lg shadow-teal-600/20" data-id="element-1530">
                    <Calendar className="w-5 h-5 flex-shrink-0" data-id="element-1531" />
                    {t('nav.bookOnline')}
                  </Button>
                </a>
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:flex-1" data-id="element-1532">
                  <Button size="lg" fullWidth className="gap-2 shadow-lg shadow-teal-600/20" data-id="element-1533">
                    <MessageCircle className="w-5 h-5 flex-shrink-0" data-id="element-1534" />
                    WhatsApp
                  </Button>
                </a>
                <div className="w-full sm:flex-1" data-id="element-1535">
                  <Button size="lg" fullWidth className="gap-2 shadow-lg shadow-teal-600/20" onClick={() => setIsCallbackOpen(true)} data-id="element-1536">
                    <Phone className="w-5 h-5 flex-shrink-0" data-id="element-1537" />
                    {lang === 'en' ? 'Request a Call' : lang === 'ua' ? 'Замовити дзвінок' : 'Заказать звонок'}
                  </Button>
                </div>
              </div>
            </div>

            <div className="relative order-1 lg:order-2" data-id="element-1538">
              <div className="absolute inset-0 bg-teal-200 rounded-3xl transform rotate-3 blur-sm opacity-30" data-id="element-1539"></div>
              <img src="https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&w=1200&q=80" alt="Beautiful smiling woman at dental clinic" className="relative rounded-3xl shadow-2xl object-cover h-[280px] sm:h-[360px] lg:h-[540px] w-full" data-id="element-1540" />
            </div>
          </div>
        </div>
      </section>

      {/* UA ONLY: Trust Signals & SEO Link */}
      {lang === 'ua' && <section className="bg-teal-50 border-y border-teal-100 py-6" data-id="element-1541">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1542">
            <div className="flex flex-wrap justify-center gap-4 md:gap-8 mb-4" data-id="element-1543">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-700" data-id="element-1544">
                <Star className="w-4 h-4 text-yellow-400 fill-current" data-id="element-1545" />
                5★ Google
              </div>
              <div className="flex items-center gap-2 text-sm font-medium text-gray-700" data-id="element-1546">
                <MapPin className="w-4 h-4 text-teal-600" data-id="element-1547" />
                Central London, Angel
              </div>
              <div className="flex items-center gap-2 text-sm font-medium text-gray-700" data-id="element-1548">
                <Scan className="w-4 h-4 text-teal-600" data-id="element-1549" />
                Цифрова стоматологія
              </div>
              <div className="flex items-center gap-2 text-sm font-medium text-gray-700" data-id="element-1550">
                <Languages className="w-4 h-4 text-teal-600" data-id="element-1551" />
                Українська мова
              </div>
            </div>
            <div className="text-center" data-id="element-1552">
              <Link to="/ua/ukrainskyi-stomatolog-london" className="text-sm font-semibold text-teal-700 hover:text-teal-800 hover:underline inline-flex items-center gap-1" data-id="element-1553">
                Шукаєте українського стоматолога в Лондоні?{' '}
                <ArrowRight className="w-3 h-3" data-id="element-1554" />
              </Link>
            </div>
          </div>
        </section>}

      {/* Visual Services Grid */}
      <section className="py-16 lg:py-20 bg-gray-50" data-id="element-1555">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1556">
          <div className="text-center max-w-xl mx-auto mb-12" data-id="element-1557">
            <h2 className="text-3xl font-bold text-gray-900 mb-3" data-id="element-1558">
              {t('services.title')}
            </h2>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6" data-id="element-1559">
            {services.map((service, idx) => <Link key={idx} to={`/${lang}/services/${service.id}`} className="group" data-id="element-1560">
                <Card className="overflow-hidden flex flex-col h-full border-0 shadow-md" data-id="element-1561">
                  <div className="relative aspect-[4/3] overflow-hidden" data-id="element-1562">
                    <img src={service.image} alt={service.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-1563" />
                  </div>
                  <div className="p-4 lg:p-5" data-id="element-1564">
                    <h3 className="text-base lg:text-lg font-bold text-gray-900 mb-1 group-hover:text-teal-600 transition-colors" data-id="element-1565">
                      {service.title}
                    </h3>
                    <p className="text-gray-500 text-xs lg:text-sm line-clamp-1" data-id="element-1566">
                      {service.desc}
                    </p>
                  </div>
                </Card>
              </Link>)}
          </div>

          <div className="mt-10 text-center" data-id="element-1567">
            <Link to={`/${lang}/services`} data-id="element-1568">
              <Button variant="secondary" size="lg" data-id="element-1569">
                {t('services.viewAll')}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 lg:py-20 bg-white" data-id="element-1570">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1571">
          <div className="text-center mb-12" data-id="element-1572">
            <h2 className="text-3xl font-bold text-gray-900 mb-3" data-id="element-1573">
              {lang === 'en' ? 'Why Choose Dentico Clinic' : lang === 'ua' ? 'Чому обирають Dentico Clinic' : 'Почему выбирают Dentico Clinic'}
            </h2>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6" data-id="element-1574">
            {advantages.map((adv, idx) => <div key={idx} className="flex flex-col items-center text-center p-5 lg:p-6 bg-gray-50 rounded-2xl" data-id="element-1575">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center text-teal-600 mb-3" data-id="element-1576">
                  <adv.icon className="w-6 h-6" data-id="element-1577" />
                </div>
                <h3 className="text-sm lg:text-base font-bold text-gray-900 mb-1" data-id="element-1578">
                  {adv.title}
                </h3>
                <p className="text-gray-500 text-xs lg:text-sm" data-id="element-1579">{adv.desc}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Location */}
      <section className="py-16 lg:py-20 bg-gray-50" data-id="element-1580">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1581">
          <div className="bg-teal-900 rounded-3xl overflow-hidden shadow-2xl" data-id="element-1582">
            <div className="grid md:grid-cols-2" data-id="element-1583">
              <div className="p-8 md:p-12 lg:p-16 flex flex-col justify-center" data-id="element-1584">
                <div className="flex items-center gap-2 text-teal-300 mb-4" data-id="element-1585">
                  <MapPin className="w-5 h-5" data-id="element-1586" />
                  <span className="font-medium tracking-wide uppercase text-sm" data-id="element-1587">
                    Central London
                  </span>
                </div>
                <h2 className="text-3xl font-bold text-white mb-4" data-id="element-1588">
                  Angel, Islington
                </h2>
                <p className="text-teal-100 mb-1 text-lg" data-id="element-1589">360–364 City Road</p>
                <p className="text-teal-100 mb-8 text-lg" data-id="element-1590">London, EC1V 2PY</p>
                <div className="flex flex-col sm:flex-row gap-3" data-id="element-1591">
                  <a href={MAPS_URL} target="_blank" rel="noopener noreferrer" data-id="element-1592">
                    <Button size="lg" className="w-full sm:w-auto gap-2" data-id="element-1593">
                      <MapPin className="w-4 h-4" data-id="element-1594" />
                      {t('location.getDirections')}
                    </Button>
                  </a>
                  <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" data-id="element-1595">
                    <Button size="lg" className="w-full sm:w-auto gap-2" data-id="element-1596">
                      <Calendar className="w-4 h-4" data-id="element-1597" />
                      {t('nav.bookOnline')}
                    </Button>
                  </a>
                </div>
              </div>
              <div className="h-64 md:h-auto bg-teal-800 relative min-h-[300px]" data-id="element-1598">
                <iframe src={MAPS_EMBED_URL} width="100%" height="100%" style={{
                border: 0
              }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Dentico Clinic Location" className="absolute inset-0" data-id="element-1599"></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Callback Form */}
      <section className="py-16 lg:py-20 bg-white" data-id="element-1600">
        <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1601">
          <CallbackForm data-id="element-1602" />
        </div>
      </section>

      {/* Reviews */}
      <GoogleReviews data-id="element-1603" />

      {/* UA ONLY: FAQ Section */}
      {lang === 'ua' && <section className="py-16 bg-white border-t border-gray-100" data-id="element-1604">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1605">
            <div className="text-center mb-10" data-id="element-1606">
              <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-1607">
                Часті запити про українського стоматолога в Лондоні
              </h2>
            </div>
            <div className="space-y-2" data-id="element-1608">
              {faqs.map((faq, idx) => <FaqAccordionItem key={idx} question={faq.q} answer={faq.a} isOpen={openFaqIndex === idx} onToggle={() => toggleFaq(idx)} data-id="element-1609" />)}
            </div>
          </div>
        </section>}

      {/* Bottom CTA */}
      <section className="py-16 bg-teal-900" data-id="element-1610">
        <div className="max-w-3xl mx-auto px-4 text-center" data-id="element-1611">
          <h2 className="text-2xl lg:text-3xl font-bold text-white mb-6" data-id="element-1612">
            {lang === 'en' ? 'Ready to book your visit?' : lang === 'ua' ? 'Готові записатись?' : 'Готовы записаться?'}
          </h2>
          <div className="flex flex-col sm:flex-row justify-center gap-3" data-id="element-1613">
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:flex-1 sm:max-w-[220px]" data-id="element-1614">
              <Button size="lg" fullWidth className="gap-2 bg-white text-teal-900 hover:bg-teal-50" data-id="element-1615">
                <Calendar className="w-5 h-5 flex-shrink-0" data-id="element-1616" />
                {t('nav.bookOnline')}
              </Button>
            </a>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:flex-1 sm:max-w-[220px]" data-id="element-1617">
              <Button variant="outline" size="lg" fullWidth className="gap-2 border-teal-400 text-teal-100 hover:bg-teal-800 hover:text-white" data-id="element-1618">
                <MessageCircle className="w-5 h-5 flex-shrink-0" data-id="element-1619" />
                WhatsApp
              </Button>
            </a>
            <div className="w-full sm:flex-1 sm:max-w-[220px]" data-id="element-1620">
              <Button variant="outline" size="lg" fullWidth className="gap-2 border-teal-400 text-teal-100 hover:bg-teal-800 hover:text-white" onClick={() => setIsCallbackOpen(true)} data-id="element-1621">
                <Phone className="w-5 h-5 flex-shrink-0" data-id="element-1622" />
                {lang === 'en' ? 'Request a Call' : lang === 'ua' ? 'Замовити дзвінок' : 'Заказать звонок'}
              </Button>
            </div>
          </div>
        </div>
      </section>
    </Layout>;
}